import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input, message } from 'antd';
import { useGraphicStandFields } from 'components/use-hooks';
import useGraphicStandMutationCreateUpdate from '../hooks/graphic-stand-use-mutation-create-update';
import './graphic-stand-new-location-field.scss';

const GraphicStandNewLocationField = ({ field, stand, graphicStand, graphicPackingId }) => {
    const [value, setValue] = useState('');

    const inputRef = useRef(null);

    // Determine which object to use - graphicStand for existing items, stand for new items
    const targetObject = graphicStand || stand;
    const isExistingStand = !!graphicStand;

    const { setFieldValue, getFieldValue, clearField, areAllFieldsFilled } = useGraphicStandFields(targetObject?.id);

    // Add mutation hook for existing stands
    const { update } = useGraphicStandMutationCreateUpdate({
        graphicStandId: isExistingStand ? targetObject?.id : undefined,
        skipRedirect: true,
        skipNotifications: true
    });

    const handleFocus = () => {
        if (inputRef.current) {
            inputRef.current.select();
        }
    };

    const handleChange = useCallback((e) => {
        const newValue = e.target.value;
        setValue(newValue);

        // If value is empty, clear field in cache immediately
        if (!newValue.trim()) {
            clearField(field);
        }
    }, [clearField, field]);

                const handleBlur = useCallback(async () => {

                // For existing stands, save immediately via mutation
        if (isExistingStand) {
            const originalValue = targetObject?.[field] || '';

            // Only save if value actually changed (including empty values)
            if (value !== originalValue) {
                // Send all required fields for update
                const input = {
                    id: targetObject.id,
                    exhibitor: targetObject.exhibitor,
                    stand_id: targetObject.stand_id,
                    hall_name: field === 'hall_name' ? value : (targetObject.hall_name || ''),
                    corridor: field === 'corridor' ? value : (targetObject.corridor || ''),
                    block: field === 'block' ? value : (targetObject.block || ''),
                    stand_number: field === 'stand_number' ? value : (targetObject.stand_number || ''),
                    graphic_list_id: targetObject.graphic_list_id,
                    status: targetObject.status || 'done'
                };

                try {
                    await update({
                        variables: {
                            input
                        }
                    });
                } catch (error) {
                    // Revert value on error and show error message
                    setValue(originalValue);
                    message.error(`Error updating ${field}: ${error.message}`);
                }
            }
        } else {
            // For new stands, save to cache
            setFieldValue(field, value);

            // Check if all fields are filled for new creation
            const allFieldsFilled = areAllFieldsFilled();
            if (allFieldsFilled) {
                // All fields are filled, ready for creation
            }
        }
    }, [isExistingStand, value, targetObject, field, update, setFieldValue, areAllFieldsFilled]);



    // Load initial value from existing data or cache
    useEffect(() => {
        const initialValue = isExistingStand
            ? (targetObject?.[field] || '')
            : (getFieldValue(field) || '');
        setValue(initialValue);
    }, [isExistingStand, targetObject, field, getFieldValue]);

    // For new stands, update cache when value changes
    useEffect(() => {
        if (!isExistingStand && value !== undefined) {
            const timer = setTimeout(() => {
                setFieldValue(field, value);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isExistingStand, value, field, setFieldValue]);

    if (!targetObject) {
        return <strong>&mdash;</strong>;
    }

    return (
        <Input
            ref={inputRef}
            id={`${field}_${targetObject.id}`}
            name={`${field}_${targetObject.id}`}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className="new-location-field-input"
            placeholder="—"
        />
    );
};

export default GraphicStandNewLocationField;
